export type RootLandingPath = "/agency" | "/welcome" | "/cohorts";

export interface SetupStep {
  step: number;
  name: string;
  required: boolean;
  complete: boolean;
}

export interface OperatorSetupNotice {
  message: string;
  action: string;
  href: string;
}

/** The agency owner always lands in the agency console. The active client may
 * still need inputs, but that should never restart the owner's own login flow. */
export function rootLandingPath(input: {
  isOperator: boolean;
  onboardingCompleted: boolean | undefined;
  previewAllowed: boolean | undefined;
}): RootLandingPath {
  if (input.isOperator) return "/agency";
  if (input.onboardingCompleted === false && input.previewAllowed !== true) return "/welcome";
  return "/cohorts";
}

/** Tell an operator what the active client account is missing, with one direct
 * action. Client onboarding language would wrongly imply the operator must
 * repeat the client's full setup. */
export function operatorSetupNotice(input: {
  isOperator: boolean;
  completed: boolean;
  accountName?: string | null;
  steps: SetupStep[];
}): OperatorSetupNotice | null {
  if (!input.isOperator || input.completed) return null;

  const account = input.accountName?.trim() || "This account";
  const missing = input.steps.find((step) => step.required && !step.complete);

  switch (missing?.step) {
    case 1:
      return {
        message: `${account} is missing basic account details.`,
        action: "Add account details →",
        href: "/welcome?step=1",
      };
    case 2:
      return {
        message: `${account} is missing an order source. Connect one before reviewing revenue.`,
        action: "Open Connections →",
        href: "/connections",
      };
    case 3:
      return {
        message: `${account} still needs product costs before its profit numbers can be reviewed.`,
        action: "Open product costs →",
        href: "/onboarding/chat?stage=costs",
      };
    case 4:
      return {
        message: `${account} is missing monthly overhead. Add it before reviewing the profit and cash numbers.`,
        action: "Add overhead →",
        href: "/costs/overhead",
      };
    case 6:
      return {
        message: `${account} still has products that need review.`,
        action: "Review products →",
        href: "/onboarding/chat?stage=categorize&review=1",
      };
    default:
      return {
        message: `${account} is not ready for team review yet.`,
        action: "Open the review step →",
        href: "/welcome?step=7",
      };
  }
}
